import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { CaseService } from '../../services/case.service';
import { PatientCaseViewDataModel } from '../../models/patient/patient-case-view-data.model';
import { PatientCaseDataModel } from '../../models/patient/patient-case-data.model';
import { StaticDataService } from '../../services/static-data.service';
import { DoctorDataModel } from '../../models/doctors/doctor-data.model';
import { DoctorSummaryModel } from '../../models/doctors/doctor-summary.model';
import { DoctorsService } from '../../services/doctors.service';
import { DoctorQueryOptionsModel } from '../../models/doctors/doctor-query-options.model';
import { TdoDropdownChange } from "../../tdo-controls/common/tdo-dropdown-v2/models/tdo-dropdown-change";
import { Globals } from "../../globals";
import { ToothPickerComponent } from 'src/app/modules/tdo-shared/tooth-picker/tooth-picker.component';
import { ToothConfigurationModel } from 'src/app/models/teeth/tooth-configuration.model';
import { CaseAddProcedureComponent } from 'src/app/modules/case-forms/case-add-procedure/case-add-procedure.component';
import { CaseForTooth, cfgCaseProcedure } from 'src/app/modules/ar/_models/ar.models';
import { PatientService } from 'src/app/modules/patient/patient.service';
import { CaseStatusEnum } from 'src/app/models/enum/case-status.enum';
import { Constants } from "../../constants";
import { PatientDoctorDataModel } from 'src/app/models/patient/patient-doctor-data.model';
import { ToothService } from 'src/app/services/tooth.service';
import { UiService } from 'src/app/services/ui.service';
import { DialogService } from 'src/app/services/dialog.service';
import { CaseFormsService } from 'src/app/modules/case-forms/case-forms.service';
import CustomStore from 'devextreme/data/custom_store';
import { firstValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';
import { DoctorTypeEnum } from 'src/app/models/enum/doctor-type.enum';
import { MessagingService } from 'src/app/services/messaging.service';
import { MessagingConstants } from 'src/app/models/MessagingConstants';

@Component({
  standalone: false,
  selector: 'app-case-edit-dialog',
  templateUrl: './case-edit-dialog.component.html',
  styleUrls: ['./case-edit-dialog.component.scss']
})
export class CaseEditDialogComponent implements OnInit, OnDestroy {
  // {caseItem: null, mode: 'newCase'};
  parentCaseId: string = '';
  model: PatientCaseViewDataModel = new PatientCaseViewDataModel();
  parentModel: PatientCaseViewDataModel = new PatientCaseViewDataModel();
  allCases: PatientCaseViewDataModel[] = [];
  private destroyed = false;
  isLoading: boolean = false;
  isNewchildCase: boolean = false;
  isEditCaseForTooth: boolean = false;

  // Validation properties
  isToothRequired: boolean = false;
  isDescriptionRequired: boolean = false;
  isDoctorRequired: boolean = false;
  isProcedureRequired: boolean = false;
  isReferringDoctorRequired: boolean = false;

  selectedTooth: ToothConfigurationModel = new ToothConfigurationModel();
  selectedCaseProcedure: cfgCaseProcedure = new cfgCaseProcedure();
  caseForToothModel: CaseForTooth;

  doctors: DoctorSummaryModel[] = [];
  doctorsRef: DoctorSummaryModel[] = [];  // referring doctors
  originalReferringDoctor: DoctorDataModel = new DoctorDataModel();
  originalDoctorId: string = null;
  selectedReferringDoctor: DoctorDataModel = null;
  doctorPredicate: (data: DoctorSummaryModel) => string = (data: DoctorSummaryModel) => {
    return 'Dr. ' + data.dataContact.FirstName + ' ' + data.dataContact.LastName;
  };
  doctorDisabledPredicate: (data: DoctorSummaryModel) => boolean = (data: DoctorSummaryModel) => {
    return !data.isActiveDoctor;
  };
  casePredicate: (data: PatientCaseViewDataModel) => string = (data: PatientCaseViewDataModel) => {
    return data.Description + ' ' + data.DateString;
  };
  dialogTitle: string = 'Add Case';
  okButtonCaption: string = 'Add';
  dialogHeight: string = '430px';
  selectReferringDoctorBoxDataSource: CustomStore = new CustomStore();
  dropDownOptions = { container: document.querySelector('.cdk-overlay-container') ?? document.body };

  constructor(private readonly dialogRef: MatDialogRef<CaseEditDialogComponent>,
    public staticDataService: StaticDataService,
    private readonly caseService: CaseService,
    private readonly doctorService: DoctorsService,
    private readonly globals: Globals,
    private readonly dialog: MatDialog,
    private readonly patientService: PatientService,
    private readonly toothService: ToothService,
    private readonly uiService: UiService,
    private readonly dialogService: DialogService,
    private readonly caseFormsService: CaseFormsService,
    private readonly messagingService: MessagingService,
    private readonly changeDetection: ChangeDetectorRef,
    @Inject(MAT_DIALOG_DATA) public data: any) {

    if (data.caseItem) {
      if (data.mode === 'editCase') {
        this.dialogTitle = 'Edit Case';     
        this.okButtonCaption = 'Save';
        this.model = data.caseItem;
        this.parentCaseId = this.model.Case.ParentPatientCaseId;
        if (this.parentCaseId)  
          this.dialogHeight = '530px';
        else
          this.dialogHeight = '500px';
        this.allCases = this.data.allCases;
        this.isNewchildCase = false;
        this.caseForToothModel = data.caseItem.Case;
        this.selectedCaseProcedure.CaseProcedureId = data.caseItem.ProcedureId;
        this.selectedCaseProcedure.ProcedureName = data.caseItem.ProcedureName;
        // data.caseItem.Case.DoctorId can be stale/wrong on this payload; ProviderDoctorId (when present) is the authoritative case-level doctor.
        if (data.caseItem.Case.ProviderDoctorId) {
          this.caseForToothModel.DoctorId = data.caseItem.Case.ProviderDoctorId;
        }
        this.caseForToothModel.ReferralDoctorId = data.caseItem.Case.ReferringDoctorId;
        this.caseForToothModel.Description = data.caseItem.Description;
        this.caseForToothModel.ToothId = data.caseItem?.DataTeethIdsList[0]?.ToothID;
        
        if (!this.toothService.allTeeth || this.toothService.allTeeth.length === 0) {
          // TODO: Things like this will need to be updated once we have a way to check settings like geoloc.
          this.toothService.getAllTeeth(ToothService.enumUnitedStatesToothGrouping).subscribe((teeth) => {
            this.selectedTooth = teeth.find(p => p.ToothId.toLowerCase() === data.caseItem?.DataTeethIdsList[0]?.ToothID.toLowerCase());
            this.caseForToothModel.PreviousToothDescription = this.selectedTooth?.cfgToothDescriptions?.[0]?.NotationDescription;
          });
        } 
        else {
          this.selectedTooth = this.toothService.allTeeth.find(p => p.ToothId.toLowerCase() === data.caseItem?.DataTeethIdsList[0]?.ToothID.toLowerCase());
          this.caseForToothModel.PreviousToothDescription = this.selectedTooth?.cfgToothDescriptions?.[0]?.NotationDescription;
        }
        console.log('selectedTooth', this.selectedTooth);
        this.caseForToothModel.PreviousToothId = data.caseItem?.DataTeethIdsList[0]?.ToothID;        
        
      } else {
        // newChildCase
        this.dialogTitle = 'Add Sub-Case';
        this.isNewchildCase = true;
        this.dialogHeight = '530px';
        this.allCases = this.data.allCases;
        this.model = new PatientCaseViewDataModel();
        this.parentModel = this.data.caseItem;
        this.model.Case = new PatientCaseDataModel();
        this.model.Case.ParentPatientCaseId = this.parentModel.PatientCaseId;
        this.model.Case.PatientId = this.globals.currentPatientId;
        this.model.Case.LOCID = Number.parseInt(this.globals.LOCID);
        this.parentCaseId = this.parentModel.Case.PatientCaseId;

        this.caseForToothModel = new CaseForTooth();
        this.caseForToothModel.PatientId = this.globals.currentPatientId;
        this.caseForToothModel.enumCaseStatusID = CaseStatusEnum.Start;
      }
    } else {
      if (data.mode === 'newCase') {
        this.model = new PatientCaseViewDataModel();
        this.model.Case = new PatientCaseDataModel();
        this.model.Case.PatientId = this.globals.currentPatientId;
        this.model.Case.LOCID = Number.parseInt(this.globals.LOCID);
      }
      this.caseForToothModel = new CaseForTooth();
      this.caseForToothModel.PatientId = this.globals.currentPatientId;
      this.caseForToothModel.enumCaseStatusID = CaseStatusEnum.Start;
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
  }

  ngOnInit(): void {
    this.isLoading = true;
    this.doctorPredicate = (data: DoctorDataModel) => {
      return 'Dr. ' + data.dataContact.FirstName + ' ' + data.dataContact.LastName;
    };

    this.casePredicate = (data: PatientCaseViewDataModel) => {
      return data.Description + ' ' + data.DateString;
    };

    // Initialize CustomStore for referring doctor select box
    this.selectReferringDoctorBoxDataSource = new CustomStore({
      key: 'DoctorId',
      load: (loadOptions) => {
        const searchValue = loadOptions.searchValue || '';

        if (!searchValue || searchValue.trim() === '') {
          return Promise.resolve([]);
        }

        return firstValueFrom(this.doctorService.searchForDoctors(searchValue).pipe(
          map(results => results.filter(doctor => doctor.isActiveDoctor))
        ));
      },
      byKey: (key) => {
        if (!key) {
          return Promise.resolve(null);
        }

        return firstValueFrom(this.doctorService.GetDataDoctorById(key))
          .then((doctor) => {
            if (doctor?.dataContact) {
              (doctor as any).Fullname = `Dr. ${doctor.dataContact.FirstName || ''} ${doctor.dataContact.LastName || ''}`.trim();
            }
            return doctor;
          });
      }
    });

    const options: DoctorQueryOptionsModel = new DoctorQueryOptionsModel();
    options.isIncludeTdo = true;
    options.isIncludeReferring = false;
    options.isActiveOnly = false;
    this.doctorService.getDoctorsSummary(options).subscribe((res) => {
      this.doctorsRef = res.toSorted((a, b) => a.dataContact.LastName?.localeCompare(b.dataContact.LastName));
      this.doctors = this.doctorsRef.filter(p => p.isActiveDoctor == true && p.enumDoctorTypeID == DoctorTypeEnum.PROVIDER);
      const assignedDoctorId = this.caseForToothModel.DoctorId;
      if (assignedDoctorId && !this.doctors.some(p => p.DoctorId?.toLowerCase() === assignedDoctorId.toLowerCase())) {
        const inactiveAssigned = this.doctorsRef.find(p => p.DoctorId?.toLowerCase() === assignedDoctorId.toLowerCase());
        if (inactiveAssigned) {
          this.doctors = [...this.doctors, inactiveAssigned];
        }
      }
      const doctor = this.getDoctor(this.caseForToothModel.DoctorId);
      this.caseForToothModel.OriginalDoctorName = doctor?.dataContact.FirstName + ' ' + doctor?.dataContact.LastName;
      this.originalDoctorId = assignedDoctorId;
      const referralDoctor = this.getDoctorRef(this.caseForToothModel.ReferralDoctorId);
      this.caseForToothModel.OriginalReferralDoctorName = referralDoctor?.dataContact.FirstName + ' ' + referralDoctor?.dataContact.LastName;
      this.caseForToothModel.OriginalenumCaseStatusDescription = this.staticDataService.getValidValueItemByItemId(this.caseForToothModel.enumCaseStatusID)?.ItemDisplayValue;
      this.isLoading = false;
      if (!this.destroyed) { this.changeDetection.detectChanges(); }
    });

    // Load patient's referring doctor and set it in the select box
    if (this.selectedReferringDoctor == null && this.data.mode !== 'editCase') {
      this.patientService.getPatientReferringDoctor(this.globals.currentPatientId).subscribe((res) => {
        if (res && res.DoctorId && res.isActiveDoctor) {
          this.originalReferringDoctor = res;
          this.caseForToothModel.ReferralDoctorId = res.DoctorId;
          this.model.Case.ReferringDoctorId = res.DoctorId;
          this.selectedReferringDoctor = res;
        }
        else {
          this.originalReferringDoctor = new DoctorDataModel();
          this.caseForToothModel.ReferralDoctorId = null;
          this.model.Case.ReferringDoctorId = null;
        }
      });
    }
  }

  onCancelClick() {
    this.dialogRef.close();
  }

  validateRequiredFields(): boolean {
    // Reset validation flags
    this.isToothRequired = false;
    this.isDescriptionRequired = false;
    this.isDoctorRequired = false;
    this.isProcedureRequired = false;
    this.isReferringDoctorRequired = false;

    let isValid = true;

    // Validate Tooth
    if (!this.caseForToothModel.ToothId || this.caseForToothModel.ToothId.trim() === '') {
      this.isToothRequired = true;
      isValid = false;
    }

    // Validate Description
    if (!this.caseForToothModel.Description || this.caseForToothModel.Description.trim() === '') {
      this.isDescriptionRequired = true;
      isValid = false;
    }

    // Validate Doctor
    if (!this.caseForToothModel.DoctorId || this.caseForToothModel.DoctorId.trim() === '') {
      this.isDoctorRequired = true;
      isValid = false;
    }
    // Validate Referring Doctor
    if (!this.caseForToothModel.ReferralDoctorId || this.caseForToothModel.ReferralDoctorId.trim() === '') {
      this.isReferringDoctorRequired = true;
      isValid = false;
    }

    // Validate Procedure
    if (this.data.mode !== 'editCase' && (!this.caseForToothModel.CaseProcedureId || this.caseForToothModel.CaseProcedureId.trim() === '')) {
      this.isProcedureRequired = true;
      isValid = false;
    }

    return isValid;
  }

  isFormValid(): boolean {
    // For case with tooth and procedure tab
    // if (this.data.mode === 'editCase') 
    //   //disable edit case functionality for now
    //   //TODO: Implement edit case functionality
    //   return false;
    // console.log('isFormValid caseForToothModel', this.caseForToothModel);
    return !!(this.caseForToothModel.ToothId && 
              this.caseForToothModel.ToothId.trim() !== '' &&
              (this.data.mode === 'editCase' || (this.caseForToothModel.CaseProcedureId && 
              this.caseForToothModel.CaseProcedureId.trim() !== '')) &&
              this.caseForToothModel.Description && 
              this.caseForToothModel.Description.trim() !== '' &&
              this.caseForToothModel.DoctorId && 
              this.caseForToothModel.DoctorId.trim() !== '' &&
              this.caseForToothModel.ReferralDoctorId && 
              this.caseForToothModel.ReferralDoctorId.trim() !== ''
            );
    
    // For other cases, always return true
    //return true;
  }

  onOkClick() {
    // Validate required fields for case with tooth and procedure
    // if (this.data.mode === 'editCase') {
    //   this.uiService.displayMessage('warning', 'Edit case functionality is not yet implemented');
    //   return;
    // }
    if (!this.validateRequiredFields()) {
      return; // Don't proceed if validation fails
    }
    
    // Check if referring doctor has changed or is first referral doctor and needs to be saved
    const referralDoctorChanged = this.selectedReferringDoctor && 
      this.originalReferringDoctor?.DoctorId && 
      this.selectedReferringDoctor.DoctorId !== this.originalReferringDoctor.DoctorId;
      
    const isFirstReferralDoctor = !this.originalReferringDoctor?.DoctorId;
    if (referralDoctorChanged || isFirstReferralDoctor) {
      this.saveReferringDoctorToPatient();
    }

    this.isLoading=true;
    if (this.isNewchildCase) { // create Blank case
      this.model.Description = this.caseForToothModel.Description;
      this.caseForToothModel.ParentPatientCaseId = this.parentCaseId;
      this.caseService.saveCaseforTooth(this.caseForToothModel).subscribe({
        next: (res) => {
        this.dialogRef.close(true);
      }, error: (err) => {
        this.uiService.displayMessage('error', 'Error creating case: ' + err.error.ExceptionMessage);
        console.error('Error saving case', err);
        this.isLoading = false;
      },
      complete: () => {
        this.isLoading = false;
        this.changeDetection.detectChanges();
      }
    });
    }
    else if (this.data.mode === 'editCase') {
      const providerDoctorChanged = !!this.originalDoctorId
        && !!this.caseForToothModel.DoctorId
        && this.caseForToothModel.DoctorId !== this.originalDoctorId;

      if (providerDoctorChanged) {
        this.dialogService.openConfirmDialog(
          'You changed the doctor for this case. Do you also want to update the doctor on all procedures in this case?',
          true,
          false
        ).subscribe((applyToProcedures) => {
          if (applyToProcedures === undefined) {
            return; // Cancel -- abort the save, let the user keep editing
          }
          this.caseForToothModel.UpdateProcedureDoctors = applyToProcedures;
          this.saveEditedCase();
        });
      } else {
        this.caseForToothModel.UpdateProcedureDoctors = false;
        this.saveEditedCase();
      }
    }
    else { // create case with tooth and procedure
      this.caseService.saveCaseforTooth(this.caseForToothModel).subscribe({
        next: (res) => {
        localStorage.setItem('isSelectedMTI', 'false');    
        this.dialogRef.close(true);
      }, error:(err) => {
        this.uiService.displayMessage('error', 'Error adding case: ' + (err.error.ExceptionMessage ?? 'Server error'));
        console.error('Error adding case', err);   
        this.isLoading = false;
        this.changeDetection.detectChanges();     
      },
      complete: () => {
        this.isLoading = false;
        this.changeDetection.detectChanges();
      }
    });
    }
    this.changeDetection.detectChanges();

  }

  private saveEditedCase(): void {
    //this.caseForToothModel.PreviousToothId = this.originalToothId;
    const doctor = this.getDoctor(this.caseForToothModel.DoctorId);
    const referralDoctor = this.getDoctorRef(this.caseForToothModel.ReferralDoctorId);
    this.caseForToothModel.DoctorName = doctor?.dataContact.FirstName + ' ' + doctor?.dataContact.LastName;
    this.caseForToothModel.ReferralDoctorName = referralDoctor?.dataContact.FirstName + ' ' + referralDoctor?.dataContact.LastName;
    this.caseForToothModel.enumCaseStatusDescription = this.staticDataService.getValidValueItemByItemId(this.caseForToothModel.enumCaseStatusID)?.ItemDisplayValue;
    this.caseForToothModel.ToothDescription = this.selectedTooth?.cfgToothDescriptions?.[0]?.NotationDescription;

    // console.log('on edit case savePatientCase caseForToothModel', this.caseForToothModel);
    this.caseService.savePatientCase(this.caseForToothModel, this.model.PatientCaseId).subscribe({
      next: (res) => {
        localStorage.setItem('isSelectedMTI', 'false');
        this.updateActiveDoctorIfNeeded();
        if (this.caseForToothModel.UpdateProcedureDoctors) {
          // the doctor changed under any procedure with a running timer -- stop those timers rather than let them keep running under the old doctor
          this.caseFormsService.stopAllTimerWatchByPatientCaseId(this.model.PatientCaseId).subscribe({
            next: () => {
              this.dialogRef.close(true);
            },
            error: (stopErr) => {
              this.uiService.displayMessage('error', 'Case saved, but failed to stop running timers: ' + (stopErr.error?.ExceptionMessage ?? 'Server error'));
              console.error('Error stopping timers', stopErr);
              this.isLoading = false;
              this.changeDetection.detectChanges();
            }
          });
        } else {
          this.dialogRef.close(true);
        }
      }, error: (err) => {
        this.uiService.displayMessage('error', 'Error saving case: ' + (err.error.ExceptionMessage ?? 'Server error'));
        console.error('Error saving case', err);
        this.isLoading = false;
        this.changeDetection.detectChanges();
      },
      complete: () => {
        this.isLoading = false;
        this.changeDetection.detectChanges();
      }
    });
  }

  // Keeps the app-wide "active doctor" (Globals + fees page label) in sync when the doctor
  // is changed on the case currently driving that context, instead of relying on the user
  // to later click a case work item (which is a stale/racy way to pick this up).
  private updateActiveDoctorIfNeeded(): void {
    if (this.model.PatientCaseId !== this.globals.currentPatientCaseId) {
      return;
    }
    if (!this.caseForToothModel.DoctorId || this.caseForToothModel.DoctorId === this.originalDoctorId) {
      return;
    }
    this.globals.currentProviderDoctorID = this.caseForToothModel.DoctorId;
    this.globals.currentProviderDoctorFullName = this.caseForToothModel.DoctorName;

    // DOCTOR_UPDATE also tells subscribers (e.g. MTI) to cascade/persist the doctor onto
    // whatever they're currently editing -- only broadcast it when the user explicitly
    // confirmed that cascade, not merely to refresh the Fees page (which already picks up
    // the Globals change via the FEES_PAGE_REFRESH sent after this dialog closes).
    if (this.caseForToothModel.UpdateProcedureDoctors) {
      this.messagingService.sendComponentMessage(MessagingConstants.DOCTOR_UPDATE, { doctorId: this.caseForToothModel.DoctorId });
    }
  }

  saveReferringDoctorToPatient() {
    if (!this.selectedReferringDoctor?.DoctorId || !this.globals.currentPatientId) {
      return;
    }

    const dataPatientDoctor: PatientDoctorDataModel = new PatientDoctorDataModel();
    dataPatientDoctor.DoctorID = this.selectedReferringDoctor.DoctorId;
    dataPatientDoctor.PatientId = this.globals.currentPatientId;
    dataPatientDoctor.isReferring = true;
    
    // Get the first specialty if available
    if (this.selectedReferringDoctor.dataDoctorSpecialties && 
        this.selectedReferringDoctor.dataDoctorSpecialties.length > 0) {
      const activeSpecialty = this.selectedReferringDoctor.dataDoctorSpecialties.find(s => !s.xauditDeleted);
      dataPatientDoctor.DoctorSpecId = activeSpecialty ? activeSpecialty.DoctorSpecId : Constants.emptyGuid;
    } else {
      dataPatientDoctor.DoctorSpecId = Constants.emptyGuid;
    }
    
    dataPatientDoctor.PracticeId = null;
    
    this.patientService.saveDataPatientReferralDoctor(dataPatientDoctor).subscribe(
      (res) => {
      },
      (err) => {
        console.error('Error saving referring doctor', err);
      }
    );
  }


  getDoctor(DoctorID: string) {
    const doc = this.doctors?.find(p => p.DoctorId?.toLowerCase() === DoctorID?.toLowerCase());
    // console.log('getDoctor DoctorID', DoctorID);
    // console.log('getDoctor doc', doc);
    if (doc) {
      return doc;
    }

    return null;
  }

  getDoctorRef(ReferringDoctorID: string) {
    const doc = this.doctorsRef?.find(p => p.DoctorId?.toLowerCase() === ReferringDoctorID?.toLowerCase());
    // console.log('getDoctorRef ReferringDoctorID', ReferringDoctorID);
    // console.log('getDoctorRef doc', doc);
    if (doc) {
      return doc;
    }

    return null;
  }

  getParentCase() {
    const cse = this.allCases.find(p => p.PatientCaseId.toLowerCase() === this.parentCaseId.toLowerCase());
    if (cse) {
      return cse;
    }
  }

  onDoctorSelectionChange(event: TdoDropdownChange) {
    // console.log('onDoctorSelectionChange event', event);
    const doctor: DoctorDataModel = event.value;
    if (doctor) {
      //this.model.Case. = doctor.DoctorId;
      this.caseForToothModel.DoctorName = doctor.dataContact.FirstName + ' ' + doctor.dataContact.LastName;
      this.caseForToothModel.DoctorId = doctor.DoctorId;
    }
  }

  onReferringDoctorValueChanged(event: any) {
    const doctorId = event.value;
    if (doctorId) {
      this.model.Case.ReferringDoctorId = doctorId;
      this.caseForToothModel.ReferralDoctorId = doctorId;
      // Get the full doctor object from doctorsRef to ensure we have specialties data
      const fullDoctor = this.doctorsRef.find(p => p.DoctorId?.toLowerCase() === doctorId?.toLowerCase());
      if (fullDoctor) {
        this.selectedReferringDoctor = fullDoctor as any;
        this.caseForToothModel.ReferralDoctorName = fullDoctor.dataContact.FirstName + ' ' + fullDoctor.dataContact.LastName;
      } else {
        // Doctor came from search results and is not in doctorsRef — fetch full object
        this.doctorService.GetDataDoctorById(doctorId).subscribe((doctor) => {
          if (doctor) {
            this.selectedReferringDoctor = doctor as any;
            if (doctor.dataContact) {
              this.caseForToothModel.ReferralDoctorName = doctor.dataContact.FirstName + ' ' + doctor.dataContact.LastName;
            }
          }
        });
      }
    } else {
      this.selectedReferringDoctor = null;
    }
  }

  onParentCaseChange(event: TdoDropdownChange) {
    const parent: PatientCaseViewDataModel = event.value;
    this.model.Case.ParentPatientCaseId = parent.PatientCaseId;
    this.parentCaseId = parent.PatientCaseId;
  }

  onOpenToothSelector() {
    const dialogRef = this.dialog.open(ToothPickerComponent, {
      panelClass: 'dialog',
      data: {
        isMultiSelect: false
      }
    });
    dialogRef.afterClosed().subscribe(result => {
      // tooth selected
      if (result) {
        console.log('onOpenToothSelector tooth selected', result);
        this.selectedTooth = result[0].toothModel;
        this.caseForToothModel.ToothId = this.selectedTooth.ToothId;
        this.changeDetection.detectChanges();
      }
    });
    this.changeDetection.detectChanges();
  }

  onOpenProcedureSelector() {
    // open a window to add a new procedure to this new case
    const dialogRef = this.dialog.open(CaseAddProcedureComponent, {
      panelClass: 'dialog',
      width: '800px',
      height: '650px',
      maxHeight: '90vh',
      data: {
        isNewCase: true,
        isSingleSelection: true
      }
    });
    dialogRef.afterClosed().subscribe(result => {
      this.selectedCaseProcedure = result;
      this.caseForToothModel.CaseProcedureId = this.selectedCaseProcedure.CaseProcedureId;
      this.caseForToothModel.Description = this.staticDataService.getValidValueItemByItemId(result.enumProcedureTypeID)?.ItemDisplayValue;
      this.changeDetection.detectChanges();
    }, err => {
      //this.selectedCaseProcedure = result;
    });
    this.changeDetection.detectChanges();

  }
}
